import React, { useState, useRef, useCallback, useEffect } from 'react'
import {
  ArrowUp,
  Square,
  FileText,
  X,
  Paperclip,
  Loader2,
  CheckCircle2,
  AlertCircle,
  User,
  Target,
  Briefcase,
  BookOpen,
} from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'
import { QuickActionChips } from './QuickActionChips'
import { SlashCommandPopover, getFilteredCommands, type SlashCommand } from './SlashCommandPopover'
import { MentionPopover, type MentionEntity } from './MentionPopover'
import { searchCrmEntities } from '../services/crmSearchApi'
import type { FileAttachment, CrmEntityType } from '../types'

interface ChatInputProps {
  onSend: (message: string) => void
  onStop?: () => void
  isStreaming?: boolean
  disabled?: boolean
  workspaceId?: string | null
  showChips?: boolean
  userRole?: string
  onClearConversation?: () => void
  onNewConversation?: () => void
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSend,
  onStop,
  isStreaming = false,
  disabled,
  workspaceId,
  showChips,
  userRole,
  onClearConversation,
  onNewConversation,
}) => {
  const { currentTheme } = useTheme()
  const [value, setValue] = useState('')
  const [focused, setFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Drag & drop state
  const [isDragOver, setIsDragOver] = useState(false)
  const [attachments, setAttachments] = useState<FileAttachment[]>([])

  // Entity Tokens state
  const [entityTokens, setEntityTokens] = useState<MentionEntity[]>([])

  // Command & mention popover states
  const [slashQuery, setSlashQuery] = useState<string | null>(null)
  const [slashActiveIndex, setSlashActiveIndex] = useState(0)

  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [mentionStart, setMentionStart] = useState(-1)
  const [mentionCategory, setMentionCategory] = useState<'all' | CrmEntityType>('all')
  const [mentionEntities, setMentionEntities] = useState<MentionEntity[]>([])
  const [mentionLoading, setMentionLoading] = useState(false)
  const [mentionActiveIndex, setMentionActiveIndex] = useState(0)
  const mentionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const adjustHeight = () => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 220)}px`
  }

  useEffect(() => {
    adjustHeight()
  }, [value])

  const parseTriggers = useCallback((text: string, cursorPos: number) => {
    if (text.startsWith('/') && cursorPos >= 1) {
      const query = text.slice(0, cursorPos)
      setSlashQuery(query)
      setSlashActiveIndex(0)
    } else {
      setSlashQuery(null)
    }

    const beforeCursor = text.slice(0, cursorPos)
    const atIdx = beforeCursor.lastIndexOf('@')
    if (atIdx !== -1 && (atIdx === 0 || beforeCursor[atIdx - 1] === ' ' || beforeCursor[atIdx - 1] === '\n')) {
      const query = beforeCursor.slice(atIdx + 1)
      if (!query.includes(' ') && !query.includes('\n')) {
        setMentionQuery(query)
        setMentionStart(atIdx)
        setMentionActiveIndex(0)
      } else {
        setMentionQuery(null)
        setMentionStart(-1)
      }
    } else {
      setMentionQuery(null)
      setMentionStart(-1)
    }
  }, [])

  // Debounced search across CRM entity types (/leads, /contacts, /deals, /kb)
  useEffect(() => {
    if (mentionQuery === null) {
      setMentionEntities([])
      return
    }

    if (mentionTimerRef.current) clearTimeout(mentionTimerRef.current)

    setMentionLoading(true)
    // Quick response for empty mention query, debounced for typed queries
    const delay = mentionQuery.length > 0 ? 250 : 40

    mentionTimerRef.current = setTimeout(async () => {
      try {
        const entities = await searchCrmEntities(workspaceId, mentionQuery, mentionCategory)
        setMentionEntities(entities)
      } catch {
        setMentionEntities([])
      } finally {
        setMentionLoading(false)
      }
    }, delay)

    return () => {
      if (mentionTimerRef.current) clearTimeout(mentionTimerRef.current)
    }
  }, [mentionQuery, workspaceId, mentionCategory])

  // Process file upload / drag-and-drop with PDF/CSV parsing status
  const handleFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files)
    fileArray.forEach((file) => {
      const id = `file_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`
      const newAttachment: FileAttachment = {
        id,
        name: file.name,
        size: file.size,
        type: file.type || 'text/plain',
        status: 'parsing',
      }

      setAttachments((prev) => [...prev, newAttachment])

      const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type.includes('csv')
      const isPdf = file.name.toLowerCase().endsWith('.pdf') || file.type.includes('pdf')

      const reader = new FileReader()

      reader.onload = (e) => {
        setTimeout(() => {
          try {
            const raw = (e.target?.result as string) || ''
            let parsedText = ''

            if (isCsv) {
              const lines = raw.split(/\r?\n/).filter((l) => l.trim().length > 0)
              const header = lines[0] || ''
              const rowCount = Math.max(lines.length - 1, 0)
              const previewLines = lines.slice(0, 50).join('\n')
              parsedText = `[CSV Data: ${file.name} | Rows: ${rowCount} | Headers: ${header}]\n${previewLines}${
                lines.length > 50 ? `\n... [${lines.length - 50} additional rows omitted for brevity]` : ''
              }`
            } else if (isPdf) {
              const textMatches = raw.match(/[\x20-\x7E\s]{4,}/g) || []
              const extracted = textMatches
                .filter((s) => !s.includes('<<') && !s.includes('>>') && !s.includes('obj') && !s.includes('endobj'))
                .slice(0, 80)
                .join(' ')
                .slice(0, 3000)

              parsedText = `[PDF Document: ${file.name} | Size: ${(file.size / 1024).toFixed(1)} KB]\n${
                extracted.length > 100
                  ? `Extracted Content:\n${extracted}`
                  : `Structured PDF content extracted from ${file.name} (${(file.size / 1024).toFixed(1)} KB). Ready for BDR context analysis.`
              }`
            } else {
              parsedText = raw.slice(0, 5000)
            }

            setAttachments((prev) =>
              prev.map((att) =>
                att.id === id ? { ...att, status: 'ready', content: parsedText } : att
              )
            )
          } catch {
            setAttachments((prev) =>
              prev.map((att) =>
                att.id === id
                  ? { ...att, status: 'error', errorMessage: 'Failed to parse file content' }
                  : att
              )
            )
          }
        }, 500)
      }

      reader.onerror = () => {
        setAttachments((prev) =>
          prev.map((att) =>
            att.id === id ? { ...att, status: 'error', errorMessage: 'File read error' } : att
          )
        )
      }

      reader.readAsText(file)
    })
  }, [])

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files)
    }
  }

  const removeAttachment = (id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  const removeEntityToken = (id: string) => {
    setEntityTokens((prev) => prev.filter((e) => e.id !== id))
  }

  const handleSend = useCallback(() => {
    if (!value.trim() && attachments.length === 0 && entityTokens.length === 0) return
    if (disabled) return
    if (attachments.some((a) => a.status === 'parsing')) return

    let finalPrompt = value.trim()

    // Inject Grounded Structured Schema Context into prompt payload
    if (entityTokens.length > 0) {
      const entityBlocks = entityTokens.map((e) => {
        const metadataLines = e.metadata
          ? Object.entries(e.metadata)
              .map(([k, v]) => `  - ${k}: ${typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
              .join('\n')
          : ''
        return `[CRM Entity Context: @${e.type.toUpperCase()}]\n- ID: ${e.id}\n- Name: ${e.title}${
          e.subtitle ? `\n- Summary: ${e.subtitle}` : ''
        }${metadataLines ? `\n- Schema Properties:\n${metadataLines}` : ''}`
      })
      finalPrompt = `${finalPrompt}\n\n=== GROUNDED CRM ENTITY CONTEXT ===\n${entityBlocks.join(
        '\n\n'
      )}\n===================================`
    }

    // Inject Attached File Context into prompt payload
    const readyAttachments = attachments.filter((a) => a.status === 'ready')
    if (readyAttachments.length > 0) {
      const fileBlocks = readyAttachments.map(
        (a) =>
          `=== ATTACHED FILE CONTEXT (${a.name} - ${(a.size / 1024).toFixed(1)} KB) ===\n${
            a.content || ''
          }\n==============================================`
      )
      finalPrompt = `${finalPrompt}\n\n${fileBlocks.join('\n\n')}`
    }

    onSend(finalPrompt)
    setValue('')
    setAttachments([])
    setEntityTokens([])
    setSlashQuery(null)
    setMentionQuery(null)
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
    }
  }, [value, attachments, entityTokens, disabled, onSend])

  const handleSlashSelect = useCallback(
    (cmd: SlashCommand) => {
      if (cmd.action === 'clear') {
        onClearConversation?.()
        setValue('')
        setSlashQuery(null)
        return
      }
      if (cmd.action === 'new') {
        onNewConversation?.()
        setValue('')
        setSlashQuery(null)
        return
      }
      setValue(cmd.prompt)
      setSlashQuery(null)
      setTimeout(() => {
        if (textareaRef.current) {
          adjustHeight()
          textareaRef.current.focus()
          textareaRef.current.selectionStart = textareaRef.current.selectionEnd = cmd.prompt.length
        }
      }, 0)
    },
    [onClearConversation, onNewConversation]
  )

  const handleMentionSelect = useCallback(
    (entity: MentionEntity) => {
      if (mentionStart === -1) return

      const before = value.slice(0, mentionStart)
      const after = value.slice(mentionStart + (mentionQuery?.length ?? 0) + 1)
      const inserted = `@${entity.title} `
      const newValue = `${before}${inserted}${after}`
      setValue(newValue)

      // Add to visual entity tokens if not already attached
      if (!entityTokens.some((e) => e.id === entity.id)) {
        setEntityTokens((prev) => [...prev, entity])
      }

      setMentionQuery(null)
      setMentionStart(-1)
      setMentionEntities([])
      setTimeout(() => {
        const ta = textareaRef.current
        if (ta) {
          const pos = before.length + inserted.length
          ta.selectionStart = ta.selectionEnd = pos
          ta.focus()
        }
      }, 0)
    },
    [value, mentionStart, mentionQuery, entityTokens]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (slashQuery !== null) {
      const cmds = getFilteredCommands(slashQuery)
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSlashActiveIndex((i) => (i + 1) % Math.max(cmds.length, 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSlashActiveIndex((i) => (i - 1 + cmds.length) % Math.max(cmds.length, 1))
        return
      }
      if (e.key === 'Enter' && !e.shiftKey && cmds.length > 0) {
        e.preventDefault()
        handleSlashSelect(cmds[slashActiveIndex]!)
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setSlashQuery(null)
        return
      }
    }

    if (mentionQuery !== null) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setMentionActiveIndex((i) => (i + 1) % Math.max(mentionEntities.length, 1))
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        setMentionActiveIndex((i) => (i - 1 + Math.max(mentionEntities.length, 1)) % Math.max(mentionEntities.length, 1))
        return
      }
      if (e.key === 'Tab') {
        e.preventDefault()
        const categories: Array<'all' | CrmEntityType> = ['all', 'lead', 'contact', 'deal', 'kb']
        const nextIdx = (categories.indexOf(mentionCategory) + 1) % categories.length
        setMentionCategory(categories[nextIdx])
        setMentionActiveIndex(0)
        return
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        if (mentionEntities.length > 0 && mentionEntities[mentionActiveIndex]) {
          handleMentionSelect(mentionEntities[mentionActiveIndex])
        }
        return
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        setMentionQuery(null)
        setMentionStart(-1)
        return
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      if (isStreaming) {
        onStop?.()
      } else {
        handleSend()
      }
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value
    setValue(newValue)
    const cursorPos = e.target.selectionStart ?? newValue.length
    parseTriggers(newValue, cursorPos)
  }

  const isParsingAny = attachments.some((a) => a.status === 'parsing')
  const canSend =
    !isParsingAny &&
    (value.trim().length > 0 || attachments.length > 0 || entityTokens.length > 0) &&
    !disabled

  const showSlash = slashQuery !== null && getFilteredCommands(slashQuery).length > 0
  const showMention = mentionQuery !== null

  const getEntityBadgeColor = (type: CrmEntityType) => {
    switch (type) {
      case 'lead':
        return 'bg-amber-500/15 text-amber-300 border-amber-500/30'
      case 'deal':
        return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
      case 'kb':
        return 'bg-sky-500/15 text-sky-300 border-sky-500/30'
      case 'contact':
      default:
        return 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30'
    }
  }

  const getEntityIcon = (type: CrmEntityType) => {
    switch (type) {
      case 'lead':
        return <Target className="w-3.5 h-3.5 text-amber-400 shrink-0" />
      case 'deal':
        return <Briefcase className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
      case 'kb':
        return <BookOpen className="w-3.5 h-3.5 text-sky-400 shrink-0" />
      case 'contact':
      default:
        return <User className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
    }
  }

  return (
    <div
      className="px-4 pb-4 pt-2 shrink-0"
      style={{ backgroundColor: currentTheme.primary }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="max-w-[60rem] mx-auto">
        {showChips && !value && (
          <QuickActionChips userRole={userRole} onChipClick={(prompt) => onSend(prompt)} />
        )}

        <div className="relative">
          {showSlash && (
            <SlashCommandPopover
              query={slashQuery!}
              onSelect={handleSlashSelect}
              onDismiss={() => setSlashQuery(null)}
              activeIndex={slashActiveIndex}
            />
          )}
          {showMention && (
            <MentionPopover
              entities={mentionEntities}
              selectedCategory={mentionCategory}
              onCategoryChange={setMentionCategory}
              loading={mentionLoading}
              onSelect={handleMentionSelect}
              onDismiss={() => {
                setMentionQuery(null)
                setMentionStart(-1)
              }}
              activeIndex={mentionActiveIndex}
            />
          )}

          <div
            className={`flex flex-col gap-2 px-5 py-3 rounded-2xl transition-all duration-200 ${
              isDragOver ? 'border-2 border-dashed border-indigo-500 bg-indigo-500/10' : ''
            }`}
            style={{
              backgroundColor: currentTheme.inputBackground,
              border: `1.5px solid ${focused ? currentTheme.accent : currentTheme.border}`,
              boxShadow: focused
                ? `0 0 0 3px rgba(79,70,229,0.15), 0 0 16px rgba(79,70,229,0.3)`
                : currentTheme.boxShadow,
            }}
          >
            {/* Entity Mention Tokens Badges with structured context preview */}
            {entityTokens.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-1.5 pb-2 border-b"
                style={{ borderColor: currentTheme.border }}
              >
                <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mr-1">
                  Context:
                </span>
                {entityTokens.map((ent) => (
                  <span
                    key={ent.id}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${getEntityBadgeColor(
                      ent.type
                    )}`}
                  >
                    {getEntityIcon(ent.type)}
                    <span className="font-semibold truncate max-w-[200px]">@{ent.title}</span>
                    <span className="text-[9px] uppercase font-mono px-1 py-0.2 rounded bg-black/20 opacity-80">
                      {ent.type}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeEntityToken(ent.id)}
                      className="p-0.5 rounded hover:bg-white/20 transition-colors ml-0.5"
                      title="Remove entity context"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* File Attachment Badges with Parsing Status */}
            {attachments.length > 0 && (
              <div
                className="flex flex-wrap items-center gap-2 pb-2 border-b"
                style={{ borderColor: currentTheme.border }}
              >
                {attachments.map((att) => (
                  <div
                    key={att.id}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      att.status === 'parsing'
                        ? 'bg-amber-500/10 border-amber-500/30 text-amber-300'
                        : att.status === 'ready'
                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                        : 'bg-rose-500/10 border-rose-500/30 text-rose-300'
                    }`}
                  >
                    <FileText
                      className={`w-3.5 h-3.5 shrink-0 ${
                        att.status === 'parsing'
                          ? 'text-amber-400'
                          : att.status === 'ready'
                          ? 'text-emerald-400'
                          : 'text-rose-400'
                      }`}
                    />
                    <span className="truncate max-w-[150px]" title={att.name}>
                      {att.name}
                    </span>
                    <span className="text-[10px] opacity-70 font-mono">
                      ({(att.size / 1024).toFixed(0)} KB)
                    </span>

                    {att.status === 'parsing' && (
                      <span className="flex items-center gap-1 text-[11px] text-amber-400 font-mono">
                        <Loader2 className="w-3 h-3 animate-spin" /> Parsing...
                      </span>
                    )}
                    {att.status === 'ready' && (
                      <span className="flex items-center gap-1 text-[11px] text-emerald-400 font-mono">
                        <CheckCircle2 className="w-3 h-3" /> Ready
                      </span>
                    )}
                    {att.status === 'error' && (
                      <span
                        className="flex items-center gap-1 text-[11px] text-rose-400 font-mono"
                        title={att.errorMessage}
                      >
                        <AlertCircle className="w-3 h-3" /> Error
                      </span>
                    )}

                    <button
                      type="button"
                      onClick={() => removeAttachment(att.id)}
                      className="p-0.5 rounded hover:bg-slate-500/20 text-slate-400 hover:text-slate-200 transition-colors ml-0.5"
                      title="Remove attachment"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-end gap-3 w-full">
              <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                multiple
                accept=".pdf,.csv,.docx,.txt,.json,.md"
                onChange={(e) => e.target.files && handleFiles(e.target.files)}
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 rounded-xl hover:bg-slate-500/10 transition-colors mb-0.5 shrink-0"
                title="Attach file (PDF, CSV, Docs)"
                style={{ color: currentTheme.mutedText }}
              >
                <Paperclip className="w-4 h-4" />
              </button>


              <textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder={
                  isStreaming
                    ? 'Zeph is responding...'
                    : 'Ask Zeph anything... (type / for commands, @ for CRM entities)'
                }
                rows={1}
                disabled={disabled && !isStreaming}
                className="flex-1 resize-none bg-transparent text-base outline-none leading-relaxed custom-scrollbar placeholder:opacity-50"
                style={{
                  color: currentTheme.text,
                  caretColor: currentTheme.accent,
                  minHeight: '28px',
                  maxHeight: '200px',
                }}
              />

              {isStreaming ? (
                <button
                  onClick={onStop}
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mb-0.5 transition-all duration-200"
                  style={{
                    background: currentTheme.accentGradient,
                    color: '#FFFFFF',
                  }}
                  aria-label="Stop generating"
                >
                  <Square className="w-4 h-4 fill-current" />
                </button>
              ) : (
                <button
                  onClick={handleSend}
                  disabled={!canSend}
                  className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 mb-0.5 transition-all duration-200"
                  style={{
                    background: canSend ? currentTheme.accentGradient : currentTheme.activeBackground,
                    color: canSend ? '#FFFFFF' : currentTheme.mutedText,
                    transform: canSend ? 'scale(1)' : 'scale(0.95)',
                  }}
                  aria-label="Send message"
                >
                  <ArrowUp className="w-[18px] h-[18px]" />
                </button>
              )}
            </div>
          </div>
        </div>

        <p className="text-center text-[11px] mt-2 select-none" style={{ color: currentTheme.mutedText }}>
          Zeph Chat Sales Operations. Verify important AI information.
        </p>
      </div>
    </div>
  )
}
